// One date picker for every report and dashboard: a pill that names the range
// ("Last 30 days", "Month to date", "Sep 1 to Sep 30") and opens presets beside
// a calendar. A preset applies at once; custom dates are picked on the calendar
// (or typed) and applied with Apply. Ranges live in the URL (see date-range.ts).
import { autoUpdate, computePosition, flip, offset, shift, size } from "@floating-ui/dom";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  AGENCY_ZONE, MAX_RANGE_DAYS, PRESET_LABEL, REPORT_PRESETS, addMonths, customError, datesLabel, monthGrid, monthTitle,
  rangeLabel, rangeParams, readDateRange, resolveRange, spanDays, startOfMonth, todayIn,
  type DateRange, type PresetKey, type RollingEnd,
} from "../date-range";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

/** The page's range from the URL, and a setter that keeps every other param. */
export function useUrlDateRange(presets: readonly PresetKey[] = REPORT_PRESETS, fallback: PresetKey = "30d", maxDays = MAX_RANGE_DAYS) {
  const [params, setParams] = useSearchParams();
  const key = `${params.get("range")}|${params.get("from")}|${params.get("to")}|${params.get("days")}`;
  const range = useMemo(() => readDateRange(params, presets, fallback, maxDays), [key]);
  const setRange = useCallback((next: DateRange) => setParams((current) => {
    const n = new URLSearchParams(current);
    for (const [k, v] of Object.entries(rangeParams(next))) if (v === null) n.delete(k); else n.set(k, v);
    return n;
  }, { replace: true }), [setParams]);
  return [range, setRange] as const;
}

type Props = {
  value: DateRange;
  onChange: (next: DateRange) => void;
  presets?: readonly PresetKey[];
  /** Where rolling windows end, for the dates shown beside them. */
  rollingEnd?: RollingEnd;
  maxDays?: number;
  /** The calendar "today" is in: the agency's, or a website's own zone. */
  zone?: string;
  /** For screen readers: what the range drives. */
  label?: string;
  /** Extra class on the pill, for a page's layout rules. */
  className?: string;
};

export default function DateRangePicker({
  value, onChange, presets = REPORT_PRESETS, rollingEnd = "yesterday", maxDays = MAX_RANGE_DAYS, zone = AGENCY_ZONE, label = "Date range", className,
}: Props) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const today = todayIn(zone);
  const current = resolveRange(value, today, rollingEnd);

  const close = useCallback((focus = true) => {
    setOpen(false);
    if (focus) trigger.current?.focus();
  }, []);
  const pick = (next: DateRange) => { onChange(next); close(); };

  // Float under the trigger, flip above it when there is no room, and never wider or taller than the screen.
  useLayoutEffect(() => {
    if (!open || !trigger.current || !panel.current) return;
    const ref = trigger.current;
    const el = panel.current;
    return autoUpdate(ref, el, () => {
      void computePosition(ref, el, {
        placement: "bottom-end",
        strategy: "fixed",
        middleware: [
          offset(6), flip({ padding: 12 }), shift({ padding: 12 }),
          size({ padding: 12, apply: ({ availableHeight }) => { el.style.maxHeight = `${Math.max(240, availableHeight)}px`; } }),
        ],
      }).then(({ x, y }) => Object.assign(el.style, { left: `${x}px`, top: `${y}px`, visibility: "visible" }));
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
    window.addEventListener("keydown", onKey, true);
    // Start on the chosen preset (or the first date field for a custom range).
    panel.current?.querySelector<HTMLElement>(".drp-preset.active, .drp-fields input")?.focus({ preventScroll: true });
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, close]);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={className ? `drp-trigger ${className}` : "drp-trigger"}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${rangeLabel(value, today)}, ${datesLabel(current.from, current.to, today)}`}
        title={datesLabel(current.from, current.to, today)}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <CalendarDays aria-hidden="true" />
        <span className="drp-trigger-label">{rangeLabel(value, today)}</span>
        <ChevronDown aria-hidden="true" className="drp-chevron" />
      </button>
      {open && createPortal(
        <>
          <div className="drp-backdrop" onClick={() => close(false)} />
          <div ref={panel} className="drp" role="dialog" aria-label={label} style={{ visibility: "hidden" }}>
            <div className="drp-presets" role="group" aria-label="Presets">
              {presets.map((key) => {
                const r = resolveRange({ key }, today, rollingEnd);
                const active = value.key === key;
                return (
                  <button key={key} type="button" className={`drp-preset${active ? " active" : ""}`} aria-pressed={active} onClick={() => pick({ key })}>
                    <span className="drp-preset-name">{PRESET_LABEL[key]}</span>
                    <span className="drp-preset-dates">{datesLabel(r.from, r.to, today)}</span>
                  </button>
                );
              })}
            </div>
            <CustomRange
              initial={current}
              today={today}
              maxDays={maxDays}
              onApply={(from, to) => pick({ key: "custom", from, to })}
              onCancel={() => close()}
            />
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

/** The calendar half: click a start day, then an end day (or type both), then Apply. */
function CustomRange({ initial, today, maxDays, onApply, onCancel }: {
  initial: { from: string; to: string };
  today: string;
  maxDays: number;
  onApply: (from: string, to: string) => void;
  onCancel: () => void;
}) {
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState<string>(initial.to);
  const [hover, setHover] = useState<string | null>(null);
  const [month, setMonth] = useState(startOfMonth(initial.to));
  const [tried, setTried] = useState(false);
  // While only the start is chosen, the hovered day previews the end.
  const end = to || (hover && hover >= from ? hover : from);
  const error = to ? customError(from, to, maxDays) : null;
  const thisMonth = startOfMonth(today);

  const clickDay = (day: string) => {
    setTried(false);
    if (!from || to) { setFrom(day); setTo(""); return; }
    if (day < from) { setTo(from); setFrom(day); } else setTo(day);
  };
  const typed = (which: "from" | "to", day: string) => {
    if (which === "from") setFrom(day); else setTo(day);
    if (day) setMonth(startOfMonth(day));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (to && !customError(from, to, maxDays)) onApply(from, to);
  };
  const count = from && to && !error ? spanDays(from, to) : null;

  return (
    <form className="drp-custom" onSubmit={submit} aria-label="Custom dates">
      <div className="drp-fields">
        <label><span>Start</span><input type="date" value={from} max={today} onChange={(e) => typed("from", e.target.value)} /></label>
        <label><span>End</span><input type="date" value={to} min={from || undefined} max={today} onChange={(e) => typed("to", e.target.value)} /></label>
      </div>
      <div className="drp-month">
        <button type="button" className="drp-nav" aria-label="Previous month" onClick={() => setMonth(addMonths(month, -1))}><ChevronLeft aria-hidden="true" /></button>
        <span className="drp-month-title" aria-live="polite">{monthTitle(month)}</span>
        <button type="button" className="drp-nav" aria-label="Next month" disabled={month >= thisMonth} onClick={() => setMonth(addMonths(month, 1))}><ChevronRight aria-hidden="true" /></button>
      </div>
      <table className="drp-grid" role="grid" onMouseLeave={() => setHover(null)}>
        <thead><tr>{WEEKDAYS.map((d) => <th key={d} scope="col">{d}</th>)}</tr></thead>
        <tbody>
          {monthGrid(month).map((week, i) => (
            <tr key={i}>
              {week.map((day, j) => {
                if (!day) return <td key={j} />;
                const inRange = !!from && day >= from && day <= end;
                const edge = day === from || day === end;
                const cls = ["drp-day", inRange && "in-range", day === from && "is-start", day === end && "is-end", edge && "is-edge", day === today && "is-today"].filter(Boolean).join(" ");
                return (
                  <td key={j}>
                    <button type="button" className={cls} disabled={day > today} aria-pressed={edge}
                      aria-label={`${monthTitle(day).split(" ")[0]} ${Number(day.slice(8))}, ${day.slice(0, 4)}`}
                      onClick={() => clickDay(day)} onMouseEnter={() => setHover(day)}>
                      {Number(day.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="drp-foot">
        <span className={`drp-status${(error || (tried && !to)) ? " is-error" : ""}`} role={error ? "alert" : undefined}>
          {error || (tried && !to ? "Pick an end date." : count ? `${count} ${count === 1 ? "day" : "days"}` : !to ? "Pick an end date" : "")}
        </span>
        <button type="button" className="button secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="button" disabled={!to || !!error}>Apply</button>
      </div>
    </form>
  );
}
